import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ChatContextItem } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';

/**
 * Fetches all chat contexts for the active brand, then returns the first
 * `ChatContextItem` whose `agent.identifier` starts with the given prefix.
 *
 * Returns `undefined` while loading, `null` if no match is found.
 */
export function useAgentChatContextItem(agentIdentifierPrefix: string): ChatContextItem | null | undefined {
  const brandIdentifier = useActiveBrandIdentifier();
  const apiClient = useChatApiClient();

  const { data: agents } = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier],
    queryFn: () => apiClient.chat.getChatContexts({ brandIdentifier }).then((res) => res.items),
    enabled: !!brandIdentifier,
  });

  return useMemo(() => {
    if (!agents) return undefined; // still loading
    return agents.find((a) => a.agent.identifier.startsWith(agentIdentifierPrefix)) ?? null;
  }, [agents, agentIdentifierPrefix]);
}
